import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { ApiError } from "../../api/http";
import { fetchEventById } from "../../api/eventsApi";
import { useOperations } from "../../state/OperationsProvider";
import { InvoiceImportReviewForm } from "./InvoiceImportReviewForm";
import { InvoicePdfViewer } from "./InvoicePdfViewer";
import { formatApiConflictMessage, useInvoiceImport } from "./InvoiceImportProvider";
import { resolveImportDisplayStatus } from "./importReviewStatus";
import { getImportReviewState } from "./importReviewState";
import { importDisplayStatusLabels } from "./types";

type MobileReviewPane = "invoice" | "review";

const MIN_PDF_PANE_WIDTH = 320;
const MIN_FORM_PANE_WIDTH = 360;

export function InvoiceImportReviewPage() {
  const { itemId: rawItemId } = useParams();
  const itemId = rawItemId ? decodeURIComponent(rawItemId) : "";
  const navigate = useNavigate();
  const { addBackendEvent, announce } = useOperations();
  const {
    getItem,
    loadImport,
    updateDraft,
    updateLine,
    markReady,
    createEventFromImport,
    saveState,
    saveError,
  } = useInvoiceImport();
  const [isCreating, setIsCreating] = useState(false);
  const [isMarkingReady, setIsMarkingReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [detailReady, setDetailReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [conflictEventId, setConflictEventId] = useState<string | null>(null);
  const [mobilePane, setMobilePane] = useState<MobileReviewPane>("review");
  const [pdfPaneWidth, setPdfPaneWidth] = useState(520);
  const splitRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const item = getItem(itemId);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setDetailReady(false);
    setLoadError(null);
    void loadImport(itemId)
      .then((loaded) => {
        if (!cancelled && !loaded) setLoadError("Invoice import not found.");
        if (!cancelled && loaded) {
          setDetailReady(Boolean(loaded.draft?.lines?.length));
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError("Could not load invoice import.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, loadImport]);

  const readOnly = Boolean(item?.importedEventId);
  const reviewState = useMemo(
    () => getImportReviewState(item, { readOnly, isCreating, isMarkingReady }),
    [item, isCreating, isMarkingReady, readOnly],
  );

  if (isLoading || !detailReady || !item) {
    return (
      <section className="invoice-import-page">
        <p className="muted">Loading review…</p>
      </section>
    );
  }

  if (loadError) {
    return <Navigate to="/events/import" replace />;
  }

  if (item.parseStatus !== "parsed" || !item.draft) {
    return (
      <section className="invoice-import-page">
        <Link className="text-button" to="/events/import">← Import queue</Link>
        <p className="invoice-import-error">This invoice is not ready for review yet.</p>
      </section>
    );
  }

  const displayStatus = resolveImportDisplayStatus(item);

  async function handleCreateEvent() {
    if (!item?.draft || item.importedEventId || isCreating || readOnly) return;
    if (!reviewState.canCreateEvent) {
      setValidationErrors(reviewState.createBlockingReasons);
      announce(reviewState.createBlockingReasons[0] ?? "Complete review before creating the Event.");
      return;
    }
    setValidationErrors([]);
    setActionError(null);
    setConflictEventId(null);
    setIsCreating(true);
    try {
      const { eventId } = await createEventFromImport(item.id);
      const event = await fetchEventById(eventId);
      await addBackendEvent(event);
      announce("Event created from invoice import");
      navigate(`/events/${eventId}`);
    } catch (error) {
      if (error instanceof ApiError) {
        setActionError(formatApiConflictMessage(error));
        if (error.eventId) setConflictEventId(error.eventId);
      } else {
        setActionError(error instanceof Error ? error.message : "Could not create Event.");
      }
    } finally {
      setIsCreating(false);
    }
  }

  async function handleMarkReady() {
    if (readOnly || !item || isMarkingReady) return;
    setActionError(null);
    setConflictEventId(null);
    try {
      await loadImport(item.id);
      setIsMarkingReady(true);
      await markReady(item.id);
      announce("Invoice marked ready");
    } catch (error) {
      if (error instanceof ApiError) {
        setActionError(formatApiConflictMessage(error));
        if (error.eventId) setConflictEventId(error.eventId);
      } else {
        setActionError(error instanceof Error ? error.message : "Could not mark ready.");
      }
    } finally {
      setIsMarkingReady(false);
    }
  }

  const formProps = {
    draft: item.draft,
    readOnly,
    onDraftChange: (draft: NonNullable<typeof item.draft>) => {
      setValidationErrors([]);
      updateDraft(item.id, draft);
    },
    onLineChange: (lineId: string, patch: Parameters<typeof updateLine>[2]) => {
      setValidationErrors([]);
      updateLine(item.id, lineId, patch);
    },
    markedReady: item.markedReady,
    reviewState,
    isMarkingReady,
    onMarkReady: () => void handleMarkReady(),
    importedEventId: item.importedEventId,
    validationErrors,
    actionError,
    conflictEventId,
    saveState,
    saveError,
    isCreating,
    onCreateEvent: () => void handleCreateEvent(),
  };

  function onDividerPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    dragRef.current = { startX: event.clientX, startWidth: pdfPaneWidth };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onDividerPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!dragRef.current || !splitRef.current) return;
    const splitWidth = splitRef.current.clientWidth;
    const maxWidth = splitWidth - MIN_FORM_PANE_WIDTH - 12;
    const nextWidth = dragRef.current.startWidth + (event.clientX - dragRef.current.startX);
    setPdfPaneWidth(Math.min(Math.max(nextWidth, MIN_PDF_PANE_WIDTH), maxWidth));
  }

  function onDividerPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    dragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  }

  return (
    <section className="invoice-import-page invoice-review-page">
      <div className="invoice-import-head">
        <Link className="text-button" to="/events/import">← Import queue</Link>
        <h1 className="invoice-import-title">Review invoice</h1>
        <p className="invoice-import-lead">
          {item.source.filename}
          {" · "}
          <span className={`import-status-pill import-status-${displayStatus}`}>
            {importDisplayStatusLabels[displayStatus]}
          </span>
          {!readOnly && saveState !== "idle" && (
            <>
              {" · "}
              <span className="invoice-import-save-state">
                {saveState === "saving" && "Saving…"}
                {saveState === "saved" && "Saved"}
                {saveState === "error" && (saveError ?? "Failed to save")}
              </span>
            </>
          )}
        </p>
      </div>

      <div className="invoice-review-workspace desktop-view">
        <div
          ref={splitRef}
          className="invoice-review-split"
          style={{ gridTemplateColumns: `${pdfPaneWidth}px 12px minmax(${MIN_FORM_PANE_WIDTH}px, 1fr)` }}
        >
          <aside className="invoice-review-pane invoice-review-pane-pdf">
            <InvoicePdfViewer importId={item.id} source={item.source} hasSourceDocument={item.hasSourceDocument !== false} />
          </aside>
          <div
            className="invoice-review-divider"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize PDF pane"
            onPointerDown={onDividerPointerDown}
            onPointerMove={onDividerPointerMove}
            onPointerUp={onDividerPointerUp}
          />
          <div className="invoice-review-pane invoice-review-pane-form">
            <InvoiceImportReviewForm {...formProps} />
          </div>
        </div>
      </div>

      <div className="invoice-review-workspace mobile-view invoice-import-mobile">
        <div className="invoice-review-mobile-tabs" role="tablist" aria-label="Review panes">
          <button
            type="button"
            role="tab"
            aria-selected={mobilePane === "invoice"}
            className={`invoice-review-mobile-tab${mobilePane === "invoice" ? " is-active" : ""}`}
            onClick={() => setMobilePane("invoice")}
          >
            Invoice
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mobilePane === "review"}
            className={`invoice-review-mobile-tab${mobilePane === "review" ? " is-active" : ""}`}
            onClick={() => setMobilePane("review")}
          >
            Review
          </button>
        </div>
        {mobilePane === "invoice" ? (
          <InvoicePdfViewer importId={item.id} source={item.source} hasSourceDocument={item.hasSourceDocument !== false} />
        ) : (
          <InvoiceImportReviewForm {...formProps} />
        )}
      </div>
    </section>
  );
}
